import React, {useEffect, useState, useCallback} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import CalciteButton from '../../shared/CalciteButton';
import CalciteModal from '../../shared/CalciteModal';
import DataService from '../../Cart/DataService';

// Constants
const LOCALE_TYPE = {
  VISITOR: 'visitor',
  CURRENT_STORE: 'currentStore',
  GLOBAL_STORE: 'globalStore',
};

const GeoblockingModal = ({geoBlockingResponse, countryCode, open, onCtaClick}) => {
  // State
  const [redirectJSON, setRedirectJSON] = useState({});

  // Helper functions
  const extractLocaleFromPath = (path) => {
    if (!path || path.length === 0) {
      return '';
    }
    const segments = path.split('/');
    for (const segment of segments) {
      if (/^[a-z]{2}-[a-z]{2}$/.test(segment)) {
        // Matches patterns like "en-us"
        return segment;
      }
    }
    return '';
  };

  const getGeoBlockingLabels = async () => {
    const pageLocale = extractLocaleFromPath(window.location.pathname);
    const isNonEnglishLocale = pageLocale?.includes('-') && !pageLocale?.includes('en-');
    const locale = isNonEnglishLocale ? pageLocale : 'en-us';

    const geoBlockingContentUrl = `${window.location.origin}/content/dam/esrisites/${locale}/content-fragments/geoblocking/labels.25.json`;
    const geoBlockingContentFragment = await DataService.getGeoBlockingContent(geoBlockingContentUrl);

    const labels = {};
    const geoLocalizeKeys = geoBlockingContentFragment?.['jcr:content']?.['data']?.['master'];
    if (geoLocalizeKeys) {
      labels.geoBlockingPurchaseOptionsTitle = geoLocalizeKeys.geoBlockingPurchaseOptionsTitle;
      labels.geoBlockingPurchaseLocalStoreMessage = geoLocalizeKeys.geoBlockingPurchaseLocalStoreMessage;
      labels.geoBlockingVisitStoreMessage = geoLocalizeKeys.geoBlockingVisitStoreMessage;
      labels.geoBlockingStayOnSiteLabel = geoLocalizeKeys.geoBlockingStayOnSiteLabel;
      labels.geoBlockingVisitStoreLabel = geoLocalizeKeys.geoBlockingVisitStoreLabel;
      labels.geoBlockingGoToStoreLabel = geoLocalizeKeys.geoBlockingGoToStoreLabel;
      labels.geoBlockingContactSalesLabel = geoLocalizeKeys.geoBlockingContactSalesLabel;
    }

    return labels;
  };

  const getLocaleType = useCallback((locale, currentDomainZone) => {
    let localeType;
    if (locale.zone === currentDomainZone) {
      localeType = LOCALE_TYPE.CURRENT_STORE;
    } else if (locale.zone === 'ROW') {
      localeType = LOCALE_TYPE.GLOBAL_STORE;
    } else {
      localeType = LOCALE_TYPE.VISITOR;
    }

    return localeType;
  }, []);

  const getStoresNames = useCallback(
    (locales, currentDomainZone) => {
      let currentStoreName = '';
      let globalStoreName = '';
      let localDistributorStoreName = '';
      let visitorZoneName = '';

      locales.forEach(function (locale) {
        const localeType = getLocaleType(locale, currentDomainZone);
        switch (localeType) {
          case LOCALE_TYPE.VISITOR:
            if (!locale.contactDistributor) localDistributorStoreName = locale.zoneName;
            visitorZoneName = locale.zoneName;
            break;
          case LOCALE_TYPE.CURRENT_STORE:
            currentStoreName = locale.zoneName;
            break;
          case LOCALE_TYPE.GLOBAL_STORE:
            globalStoreName = locale.zoneName;
            break;
        }
      });

      return {
        currentStoreName: currentStoreName,
        globalStoreName: globalStoreName,
        localDistributorStoreName: localDistributorStoreName,
        visitorZoneName: visitorZoneName,
      };
    },
    [getLocaleType],
  );

  const processGeoBlockingLocales = useCallback(
    (geoBlockingResponse, redirectJSONLabels, countryCode) => {
      const currentDomainZone = geoBlockingResponse.currentDomainZone;
      const locales = geoBlockingResponse.locales || [];
      const {localDistributorStoreName} = getStoresNames(locales, currentDomainZone);
      const isEUCustomer
        = geoBlockingResponse.locales?.find(
          (locale) => locale.eu && getLocaleType(locale, currentDomainZone) === LOCALE_TYPE.VISITOR,
        ) != null;
      const isEUStore
        = geoBlockingResponse.locales?.find(
          (locale) => locale.eu && getLocaleType(locale, currentDomainZone) === LOCALE_TYPE.CURRENT_STORE,
        ) != null;
      const isGlobalStore = geoBlockingResponse.currentDomainZone === 'ROW';

      const output = {
        bttns: [],
      };

      geoBlockingResponse.locales?.forEach(function (locale) {
        const localeType = getLocaleType(locale, currentDomainZone);

        if (localeType === LOCALE_TYPE.VISITOR && (locale.isRedirect || locale.contactDistributor)) {
          const label = redirectJSONLabels.geoBlockingContactSalesLabel.replace('{storeName}', locale.zoneName);
          const hostname = window.location.hostname;
          let baseUrl = 'https://www.esri.com/en-us/contact';
          if (hostname.includes('dev')) {
            baseUrl = 'https://aem-dev.esri.com/en-us/contact';
          } else if (hostname.includes('uat')) {
            baseUrl = 'https://uat.esri.com/en-us/contact';
          }

          output.bttns.push({
            label,
            link: `${baseUrl}#t=1${countryCode ? `&c=${countryCode}` : ''}`,
            locale: locale,
            contact: true,
            sort: 2,
          });
        }

        if (
          (localeType === LOCALE_TYPE.VISITOR
            || (localeType === LOCALE_TYPE.GLOBAL_STORE && !localDistributorStoreName))
          && locale.isRedirect
        ) {
          const label = redirectJSONLabels.geoBlockingVisitStoreLabel.replace('{storeName}', locale.zoneName);
          const secondaryLabel = redirectJSONLabels.geoBlockingGoToStoreLabel.replace('{storeName}', locale.zoneName);
          output.bttns.push({
            label,
            link: locale.redirect_uri,
            locale,
            primary: true,
            sort: 3,
            ...(secondaryLabel && {secondaryLabel}),
          });
        } else if (
          localeType === LOCALE_TYPE.CURRENT_STORE
          && isEUCustomer
          && ((isEUStore && !locale.optOutGeoBlock) || isGlobalStore)
        ) {
          const label = redirectJSONLabels.geoBlockingStayOnSiteLabel;
          output.bttns.push({
            label,
            locale,
            sort: 1,
          });
        }
      });
      output.bttns.sort((a, b) => a.sort - b.sort);

      return output;
    },
    [getLocaleType, getStoresNames],
  );

  const processGeoBlockingLabels = useCallback(
    (geoBlockingResponse, redirectJSONLabels) => {
      const locales = geoBlockingResponse.locales || [];
      const currentDomainZone = geoBlockingResponse.currentDomainZone;
      const {localDistributorStoreName, globalStoreName} = getStoresNames(locales, currentDomainZone);

      const modalTitle = redirectJSONLabels.geoBlockingPurchaseOptionsTitle;
      const storeName = localDistributorStoreName || globalStoreName;
      const visitStoreMessage = redirectJSONLabels.geoBlockingVisitStoreMessage.replace('{storeName}', storeName);
      const purchaseLocalStoreMessage = redirectJSONLabels.geoBlockingPurchaseLocalStoreMessage.replace(
        '{storeName}',
        storeName,
      );
      const openCtaLabel = redirectJSONLabels.geoBlockingPurchaseOptionsLabel;

      return {
        modalTitle,
        visitStoreMessage,
        purchaseLocalStoreMessage,
        openCtaLabel,
      };
    },
    [getStoresNames],
  );

  // Side effects
  useEffect(() => {
    const fetchGeoBlockingLabels = async () => {
      const labels = await getGeoBlockingLabels();
      setRedirectJSON({labels: labels});
    };

    fetchGeoBlockingLabels();
  }, []);

  useEffect(() => {
    if (!geoBlockingResponse || !geoBlockingResponse.locales) {
      return;
    }

    const geoBlockingLocales = processGeoBlockingLocales(geoBlockingResponse, redirectJSON.labels, countryCode);
    const geoBlockingLabels = processGeoBlockingLabels(geoBlockingResponse, redirectJSON.labels);
    setRedirectJSON((previousState) => ({
      ...previousState,
      ...geoBlockingLocales,
      ...geoBlockingLabels,
    }));
  }, [geoBlockingResponse, countryCode, redirectJSON.labels, processGeoBlockingLocales, processGeoBlockingLabels]);

  // Render
  return ReactDOM.createPortal(
    <CalciteModal
      className="geoblocking-modal"
      closeButtonDisabled={true}
      outsideCloseDisabled={true}
      escapeDisabled={true}
      open={open}
    >
      <div slot="header">{redirectJSON.modalTitle}</div>
      <div slot="content">{redirectJSON.purchaseLocalStoreMessage}</div>
      <div slot="content-bottom" style={{width: '100%'}}>
        <div className="geoblocking-modal__content-bottom-container">
          {redirectJSON.bttns?.map((button, index) => {
            const link = button.link;
            return (
              <div className="geoblocking-modal__button-container" key={index}>
                <CalciteButton
                  appearance={button.primary ? 'solid' : 'outline'}
                  data-zone={button.zone}
                  data-geoblocking-continue={!link}
                  onClick={() => {
                    onCtaClick(button.locale, button.link, button.contact);
                  }}
                >
                  {button.label}
                </CalciteButton>
              </div>
            );
          })}
        </div>
      </div>
    </CalciteModal>,
    document.body,
  );
};

GeoblockingModal.propTypes = {
  geoBlockingResponse: PropTypes.object.isRequired,
  countryCode: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onCtaClick: PropTypes.func.isRequired,
};

export default GeoblockingModal;
